import React, { useEffect, useState, useCallback } from "react";
import { Card, Table, Button, Pagination, Nav, Spinner } from "react-bootstrap";
import { IoIosArrowForward, IoIosArrowDown } from "react-icons/io";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FaExclamationCircle } from "react-icons/fa";
import Select from "react-select";
import { TableSkeleton } from "../../skeleton/TableSkeleton";
import { useTableData, usePagination } from "../../context/TableContext";
import {
  getTsaAssetData,
  exportTsaBillingDataToXLSX,
  reportCenterExportPolling,
} from "../../service/api";
import TooltipWrapper from "../../helper/Tooltip";
import { showNotification } from "../../service/showNotification";
import styles from "./index.module.css";
import { tracker } from "../../service/mixpanel";
import { useExport } from "../../context/ExportContext";
import { exportJobName } from "../../helper/ExportFile/data";

const columns = [
  { id: "assetId", label: "Asset ID" },
  { id: "dealName", label: "Deal Name" },
  { id: "billOfSaleSigned", label: "Bill of Sale Signed" },
  { id: "entityName", label: "Entity Name" },
];

const COL_LENGTH = columns.length + 1;

const TsaBilling = () => {
  const { addJob } = useExport();
  const tableContext = useTableData();
  const paginationContext = usePagination();

  const [localSearch, setLocalSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);

  const sortBy = tableContext?.sortBy || "calculatedAt";
  const sortOrder = tableContext?.sortOrder || "desc";
  const itemsPerPage = paginationContext?.itemsPerPage || 10;

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPageState, setItemsPerPageState] = useState(10);

  const [isExporting, setIsExporting] = useState(false);

  const paginationOptions = [
    { value: 5, label: "5" },
    { value: 10, label: "10" },
    { value: 25, label: "25" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
  ];

  const [isColLoader, setIsColLoader] = useState(true);
  const [isRowLoader, setIsRowLoader] = useState(true);
  const [isAssetsLoading, setIsAssetsLoading] = useState(false);

  const [assets, setAssets] = useState([]);
  const [expandedRowId, setExpandedRowId] = useState(null);

  const combinedLoading = (isColLoader && isRowLoader) || isAssetsLoading;

  const handleSearchInput = (e) => {
    setSearchTerm(e.target.value);
    tracker.tsaBilling.tsaBillingRecordSearch(searchTerm);
  };

  const generateSimpleTooltip = (mismatchValue) => {
    if (!mismatchValue || mismatchValue === "") return "";

    const mismatchItems = mismatchValue
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    return (
      <div style={{ maxWidth: "300px" }}>
        <div
          style={{ fontWeight: "bold", marginBottom: "8px", color: "#dc3545" }}
        >
          Mismatch Detected
        </div>
        <div style={{ fontSize: "12px" }}>
          {mismatchItems.length === 1 ? (
            <div>{mismatchItems[0]}</div>
          ) : (
            <ol style={{ margin: 0, paddingLeft: "16px" }}>
              {mismatchItems.map((item, index) => (
                <li key={index} style={{ marginBottom: "2px" }}>
                  {item}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setLocalSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  // Export function
  const handleExport = async () => {
    tracker.tsaBilling.clickOnExportXls();
    if (isExporting) return;

    setIsExporting(true);

    try {
      const res = await exportTsaBillingDataToXLSX({
        search: localSearch.trim(), // THIS IS THE KEY ADDITION
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (!res.data?.jobId) throw new Error("Job Id not found");

      addJob({
        id: res.data.jobId,
        name: exportJobName.tsaBilling,
      });

      // Show different success messages based on search
      const message = localSearch.trim()
        ? `Export started for search: "${localSearch.trim()}"`
        : "Export started for all data";

      showNotification("success", message);
    } catch (error) {
      console.error("Export failed:", error);
      showNotification("error", "Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const fetchData = useCallback(async () => {
    setIsAssetsLoading(true);
    setIsRowLoader(true);

    try {
      const res = await getTsaAssetData(
        currentPage,
        itemsPerPageState,
        localSearch.trim(),
        sortBy,
        sortOrder
      );

      const assetsData = res?.data?.assets || [];
      const pagination = res?.data?.pagination || {};

      setAssets(assetsData);
      setTotalPages(pagination.totalPages || 1);
      setTotalRecords(pagination.totalAssets || assetsData.length);

      setIsColLoader(false);
      setIsRowLoader(false);
    } catch (error) {
      console.error("Failed to fetch assets:", error);
      setAssets([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setIsAssetsLoading(false);
    }
  }, [currentPage, itemsPerPageState, localSearch, sortBy, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (selectedOption) => {
    if (selectedOption) {
      setItemsPerPageState(selectedOption.value);
      setCurrentPage(1);
    }
  };

  const showPageNumbers = (page, currentPage, totalPages) => {
    const pageNumber = page + 1;
    if (pageNumber === 1 || pageNumber === totalPages) return false;
    return pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2;
  };

  // Alternative: If your context expects a different pattern

  // Updated expand row function - no need for API call, data is already available
  const toggleExpand = (assetIndex) => {
    if (expandedRowId === assetIndex) {
      setExpandedRowId(null);
      return;
    }

    setExpandedRowId(assetIndex);
    // No need for API call - billing data is already in assets[assetIndex].billingTableData
  };

  useEffect(() => {
    tracker.tsaBilling.pageView();
  }, []);

  const parseNumber = (val) => {
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      const cleaned = val.replace(/[^0-9.-]/g, "");
      return parseFloat(cleaned) || 0;
    }
    return 0;
  };

  return (
    <>
      <div className="d-xl-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4 mx-3">
        {/* Updated toolbar with search and export button */}
        <div
          className="asset-toolbar d-flex align-items-center gap-2"
          style={{ width: "100%" }}
        >
          <input
            type="text"
            placeholder="Deal Name, Asset ID"
            value={searchTerm}
            onChange={handleSearchInput}
            style={{ minWidth: "250px" }}
          />
          {localSearch && (
            <Button
              className="bg-light text-black"
              onClick={() => {
                setSearchTerm("");
                setLocalSearch("");
              }}
            >
              Clear
            </Button>
          )}
          <Button
            onClick={handleExport}
            disabled={isExporting || combinedLoading || assets.length === 0}
            className="d-flex align-items-center"
          >
            {isExporting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Exporting...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Export XLSX
                {localSearch.trim() && (
                  <small className="ms-1 text-muted">(filtered)</small>
                )}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Search Status */}
      {localSearch && (
        <div className="mx-3 mb-3">
          <small className="text-muted">
            Searching for: "<strong>{localSearch}</strong>" | Showing{" "}
            {assets.length} assets
            {searchTerm && searchTerm !== localSearch && (
              <>
                {" "}
                | <em>Typing...</em>
              </>
            )}
          </small>
        </div>
      )}

      {totalRecords > 0 && (
        <div className="mx-3 mb-3">
          <small className="text-info">
            Export will include all billing calculation data for {totalRecords}{" "}
            total assets
            {localSearch && ` matching "${localSearch}"`}
          </small>
        </div>
      )}

      <Card border="light" className="shadow-sm mx-3">
        <Card.Body className="pt-0 p-0" style={{ overflowX: "auto" }}>
          {combinedLoading ? (
            <TableSkeleton />
          ) : (
            <div className="table-scroll-container">
              <Table
                hover
                className="user-table align-items-center table-striped mb-0"
              >
                <thead>
                  <tr>
                    <th></th>
                    {columns.map((h) => (
                      <th key={h.id}>{h.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {assets.length === 0 ? (
                    <tr>
                      <td colSpan={COL_LENGTH} className="text-center py-4">
                        {localSearch ? (
                          <>
                            No results found for "<strong>{localSearch}</strong>
                            "
                          </>
                        ) : (
                          "No Data Found"
                        )}
                      </td>
                    </tr>
                  ) : (
                    assets.map((asset, index) => {
                      const assetSummary = asset.assetSummary;
                      const originalBillingRows = asset.billingTableData || [];

                      const commonColumns = [
                        "Status",
                        "Asset ID",
                        "Deal Name",
                        "Bill of Sale Signed",
                        "TSA Billing Type",
                        "Client Name",
                        "Entity Name",
                        "Bill of Sale Year",
                        "Bill of Sale Month",
                        "PV KW",
                        "Storage KW",
                      ];

                      const allKeys =
                        originalBillingRows.length > 0
                          ? Object.keys(originalBillingRows[0])
                          : [];
                      const periodColumns = allKeys.filter(
                        (k) =>
                          !commonColumns.includes(k) &&
                          k !== "Mismatch Detected" &&
                          k !== "Billing Part" &&
                          k !== "Total Billings"
                      );

                      const templateRow = originalBillingRows[0] || {};
                      const firstRow = originalBillingRows[0];
                      const lastRow =
                        originalBillingRows[originalBillingRows.length - 1];

                      const topStub = firstRow
                        ? {
                            ...templateRow,
                            isStub: true,
                            "Billing Year": firstRow["Billing Year"],
                          }
                        : { ...templateRow, isStub: true };

                      const bottomStub = lastRow
                        ? {
                            ...templateRow,
                            isStub: true,
                            "Billing Year": lastRow["Billing Year"],
                          }
                        : { ...templateRow, isStub: true };
                      const displayRows = [
                        topStub,
                        ...originalBillingRows,
                        bottomStub,
                      ];

                      return (
                        <React.Fragment key={assetSummary.assetId}>
                          <tr>
                            <td
                              onClick={() => toggleExpand(index)}
                              style={{ cursor: "pointer", width: "40px" }}
                            >
                              {expandedRowId === index ? (
                                <IoIosArrowDown size={18} />
                              ) : (
                                <IoIosArrowForward size={18} />
                              )}
                            </td>
                            {columns.map((col) => (
                              <td key={col.id}>
                                {assetSummary[col.id] ?? "-"}
                              </td>
                            ))}
                          </tr>

                          {expandedRowId === index && (
                            <tr>
                              <td colSpan={COL_LENGTH} className="p-0">
                                <div className={styles.expandedTableContainer}>
                                  <div
                                    className={styles.scrollableTableWrapper}
                                  >
                                    <Table
                                      hover
                                      className="billing-table align-items-center mb-0"
                                      style={{
                                        fontSize: "12px",
                                        minWidth: "1600px", // Ensures horizontal scroll appears
                                        width: "max-content",
                                      }}
                                    >
                                      <thead className={styles.stickyHeader}>
                                        <tr>
                                          <th className={styles.headCellStyle}>
                                            Mismatch Detected
                                          </th>
                                          {commonColumns.map((col, idx) => (
                                            <th
                                              key={`common-head-${idx}`}
                                              className={styles.headCellStyle}
                                            >
                                              {col}
                                            </th>
                                          ))}
                                          <th
                                            className={styles.headCellStyle}
                                            style={{
                                              backgroundColor: "#f1f1f1",
                                            }}
                                          >
                                            Period
                                          </th>
                                          {periodColumns.map((col, idx) => (
                                            <th
                                              key={`period-head-${idx}`}
                                              className={styles.headCellStyle}
                                              style={{
                                                backgroundColor: "#f1f1f1",
                                              }}
                                            >
                                              {col}
                                            </th>
                                          ))}
                                          <th
                                            className={styles.headCellStyle}
                                            style={{
                                              fontWeight: "bold",
                                              backgroundColor: "#e9ecef",
                                            }}
                                          >
                                            Total
                                          </th>
                                        </tr>
                                      </thead>

                                      <tbody>
                                        {displayRows.map(
                                          (billingRow, rowIdx) => {
                                            const isStubRow =
                                              billingRow.isStub === true;
                                            const hasMismatch =
                                              billingRow["Mismatch Detected"];

                                            const pvTsa = parseNumber(
                                              billingRow["PV TSA Cost"]
                                            );
                                            const storageTsa = parseNumber(
                                              billingRow["Storage TSA Cost"]
                                            );
                                            const pvPc = parseNumber(
                                              billingRow["PV P&C Cost"]
                                            );
                                            const storagePc = parseNumber(
                                              billingRow["Storage P&C Cost"]
                                            );
                                            const rowTotal =
                                              pvTsa +
                                              storageTsa +
                                              pvPc +
                                              storagePc;

                                            return (
                                              <tr
                                                key={`billing-row-${rowIdx}`}
                                                style={{
                                                  backgroundColor:
                                                    hoveredRowIndex === rowIdx
                                                      ? "#e2e6ea"
                                                      : rowIdx % 2 === 0
                                                      ? "#ffffff"
                                                      : "#f9f9f9",
                                                }}
                                                onMouseEnter={() =>
                                                  setHoveredRowIndex(rowIdx)
                                                }
                                                onMouseLeave={() =>
                                                  setHoveredRowIndex(null)
                                                }
                                              >
                                                <td
                                                  className={styles.cellStyle}
                                                  style={{
                                                    backgroundColor: hasMismatch
                                                      ? "#fff5f5"
                                                      : undefined,
                                                  }}
                                                >
                                                  {hasMismatch && (
                                                    <TooltipWrapper
                                                      tooltip={generateSimpleTooltip(
                                                        billingRow[
                                                          "Mismatch Detected"
                                                        ]
                                                      )}
                                                      placement="right"
                                                    >
                                                      <FaExclamationCircle className="text-danger" />
                                                    </TooltipWrapper>
                                                  )}
                                                </td>

                                                {commonColumns.map((col) => (
                                                  <td
                                                    key={`common-${rowIdx}-${col}`}
                                                    className={styles.cellStyle}
                                                  >
                                                    {billingRow[col] ?? "-"}
                                                  </td>
                                                ))}

                                                <td
                                                  className={styles.cellStyle}
                                                  style={{ fontWeight: "bold" }}
                                                >
                                                  {(rowIdx % 2) + 1}
                                                </td>

                                                {periodColumns.map((col) => (
                                                  <td
                                                    key={`period-${rowIdx}-${col}`}
                                                    className={styles.cellStyle}
                                                  >
                                                    {isStubRow &&
                                                    col !== "Billing Year"
                                                      ? "-"
                                                      : billingRow[col] ?? "-"}
                                                  </td>
                                                ))}

                                                <td
                                                  className={styles.cellStyle}
                                                  style={{
                                                    fontWeight: "bold",
                                                    backgroundColor: "#f8f9fa",
                                                  }}
                                                >
                                                  {isStubRow
                                                    ? "-"
                                                    : "$" + rowTotal.toFixed(2)}
                                                </td>
                                              </tr>
                                            );
                                          }
                                        )}
                                      </tbody>
                                    </Table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>

        <Card.Footer
          className="px-3 border-0 d-lg-flex justify-content-between"
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#fff",
            zIndex: 10,
          }}
        >
          <Nav>
            <Pagination className="mb-2 mb-lg-0">
              <Pagination.Prev
                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Pagination.Prev>

              {totalPages > 1 && (
                <>
                  {totalPages <= 10 ? (
                    [...Array(totalPages)].map((_, page) => (
                      <Pagination.Item
                        key={page + 1}
                        active={page + 1 === currentPage}
                        onClick={() => handlePageChange(page + 1)}
                      >
                        {page + 1}
                      </Pagination.Item>
                    ))
                  ) : (
                    <>
                      <Pagination.Item
                        active={currentPage === 1}
                        onClick={() => handlePageChange(1)}
                      >
                        1
                      </Pagination.Item>

                      {currentPage > 4 && <Pagination.Ellipsis disabled />}

                      {[...Array(totalPages)].map((_, page) => {
                        if (showPageNumbers(page, currentPage, totalPages)) {
                          return (
                            <Pagination.Item
                              key={page + 1}
                              active={page + 1 === currentPage}
                              onClick={() => handlePageChange(page + 1)}
                            >
                              {page + 1}
                            </Pagination.Item>
                          );
                        }
                        return null;
                      })}

                      {currentPage < totalPages - 3 && (
                        <Pagination.Ellipsis disabled />
                      )}

                      <Pagination.Item
                        active={currentPage === totalPages}
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </Pagination.Item>
                    </>
                  )}
                </>
              )}

              <Pagination.Next
                onClick={() =>
                  handlePageChange(Math.min(currentPage + 1, totalPages))
                }
                disabled={currentPage === totalPages || totalPages === 0}
              >
                Next
              </Pagination.Next>
            </Pagination>
          </Nav>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            Items Per Page
            <Select
              options={paginationOptions}
              value={paginationOptions.find(
                (opt) => opt.value === itemsPerPageState
              )}
              onChange={handleItemsPerPageChange}
              styles={{
                control: (provided) => ({
                  ...provided,
                  minHeight: "32px",
                  height: "32px",
                  fontSize: "14px",
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  height: "32px",
                  padding: "0 6px",
                }),
                indicatorsContainer: (provided) => ({
                  ...provided,
                  height: "32px",
                }),
                option: (provided) => ({
                  ...provided,
                  fontSize: "14px",
                  padding: "6px 12px",
                  minHeight: "28px",
                }),
                menuList: (provided) => ({
                  ...provided,
                  paddingTop: 0,
                  paddingBottom: 0,
                }),
              }}
              menuPlacement="top"
            />
          </div>
        </Card.Footer>
      </Card>
    </>
  );
};

export default TsaBilling;
